import { LucideIcon, Database, Layout, Terminal, Settings2, Key, Code2, Sparkles, Bot, Download, Shield, Gift, Tags, Map, PackageOpen, FlaskConical, Cloud, Keyboard } from 'lucide-react';
import { cn } from '../../../../lib/utils';

export type SettingsSection =
  | 'connections'
  | 'azure-storage'
  | 'sessions'
  | 'snippets'
  | 'ui'
  | 'keyboard-shortcuts'
  | 'ai-terminal'
  | 'ai-integrations'
  | 'updates'
  | 'whats-new'
  | 'privacy'
  | 'advanced'
  | 'meta-info'
  | 'spatial'
  | 'backup-transfer'
  | 'beta-features'
  | 'license';

interface SettingsMenuItem {
  id: SettingsSection;
  label: string;
  icon: LucideIcon;
  testId: string;
}

const menuItems: SettingsMenuItem[] = [
  { id: 'license', label: 'Account', icon: Key, testId: 'settings-menu-license' },
  { id: 'ui', label: 'UI', icon: Sparkles, testId: 'settings-menu-ui' },
  { id: 'keyboard-shortcuts', label: 'Keyboard Shortcuts', icon: Keyboard, testId: 'settings-menu-keyboard-shortcuts' },
  { id: 'connections', label: 'Connections', icon: Database, testId: 'settings-menu-connections' },
  { id: 'azure-storage', label: 'Azure Storage', icon: Cloud, testId: 'settings-menu-azure-storage' },
  // The AI sections sit directly under Connections rather than down near
  // Updates: they are the settings people actually go looking for, and burying
  // them below Sessions/Snippets/Meta Info/Spatial made the app's AI setup feel
  // like an afterthought. AI Integrations (MCP) comes first because it is the
  // one a user must visit to let an agent talk to the app at all.
  { id: 'ai-integrations', label: 'AI Integrations', icon: Bot, testId: 'settings-menu-ai-integrations' },
  { id: 'ai-terminal', label: 'AI & Terminal', icon: Terminal, testId: 'settings-menu-ai-terminal' },
  { id: 'sessions', label: 'Sessions', icon: Layout, testId: 'settings-menu-sessions' },
  { id: 'snippets', label: 'Snippets', icon: Code2, testId: 'settings-menu-snippets' },
  { id: 'meta-info', label: 'Meta Info', icon: Tags, testId: 'settings-menu-meta-info' },
  { id: 'spatial', label: 'Spatial', icon: Map, testId: 'settings-menu-spatial' },
  // Sits just above Updates: it is a maintenance action, reached when you are
  // setting a machine up or replacing one, not a preference you tweak.
  { id: 'backup-transfer', label: 'Backup & Transfer', icon: PackageOpen, testId: 'settings-menu-backup-transfer' },
  { id: 'updates', label: 'Updates', icon: Download, testId: 'settings-menu-updates' },
  { id: 'whats-new', label: "What's New", icon: Gift, testId: 'settings-menu-whats-new' },
  { id: 'privacy', label: 'Privacy', icon: Shield, testId: 'settings-menu-privacy' },
  // Directly above Advanced: both are "change how the app behaves" rather
  // than a preference, and a user hunting for a feature they can't find
  // scans the bottom of this list.
  { id: 'beta-features', label: 'Beta Features', icon: FlaskConical, testId: 'settings-menu-beta-features' },
  { id: 'advanced', label: 'Advanced', icon: Settings2, testId: 'settings-menu-advanced' },
];

interface SettingsMenuProps {
  selectedSection: SettingsSection;
  onSelectSection: (section: SettingsSection) => void;
}

export function SettingsMenu({ selectedSection, onSelectSection }: SettingsMenuProps) {
  return (
    <nav className="flex flex-col gap-1 w-48 shrink-0">
      {menuItems.map((item) => {
        const Icon = item.icon;
        const isSelected = selectedSection === item.id;

        return (
          <button
            key={item.id}
            onClick={() => onSelectSection(item.id)}
            data-testid={item.testId}
            className={cn(
              'flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors text-left',
              isSelected
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
